import { useMotionValue, useSpring, useTransform } from 'framer-motion'
import * as React from 'react'
import { watchAttention } from './aida-icon-attention'
import {
  axisOffset,
  GAZE_MAX_X,
  GAZE_MAX_Y,
  pointerEngaged,
  pointerX,
  pointerY,
  subscribePointerTracking,
} from './aida-icon-gaze'
import { AIDA_GAZE_SPRING } from './aida-icon-animated-paths'
import { measureCenter } from './aida-icon-measure-center'

function offsetFromLatest(latest: unknown[], axis: 'x' | 'y') {
  const [px, py, cx, cy, engaged, active, looking] = latest.map(Number)
  if (engaged === 0 || active === 0 || looking === 0) {
    return 0
  }
  const dx = px - cx
  const dy = py - cy
  const distance = Math.hypot(dx, dy)
  return axisOffset(axis === 'x' ? dx : dy, distance, axis === 'x' ? GAZE_MAX_X : GAZE_MAX_Y)
}

export function useLivingEyes(
  svgRef: React.RefObject<SVGSVGElement | null>,
  track: boolean,
  blink: boolean,
) {
  const centerX = useMotionValue(0)
  const centerY = useMotionValue(0)
  const looking = useMotionValue(0)
  const lidScale = useMotionValue(1)
  const live = useMotionValue(track ? 1 : 0)

  React.useEffect(() => {
    live.set(track ? 1 : 0)
    if (!track) {
      looking.set(0)
    }
  }, [looking, live, track])

  React.useEffect(() => {
    if (!track) {
      return
    }
    return measureCenter(svgRef.current, centerX, centerY)
  }, [centerX, centerY, svgRef, track])

  React.useEffect(() => {
    if (!track) {
      return
    }
    return subscribePointerTracking()
  }, [track])

  React.useEffect(() => {
    if (!track) {
      lidScale.set(1)
      return
    }
    return watchAttention(lidScale, looking, centerX, centerY, blink)
  }, [blink, centerX, centerY, lidScale, looking, track])

  const rawX = useTransform(
    [pointerX, pointerY, centerX, centerY, pointerEngaged, live, looking],
    latest => offsetFromLatest(latest, 'x'),
  )
  const rawY = useTransform(
    [pointerX, pointerY, centerX, centerY, pointerEngaged, live, looking],
    latest => offsetFromLatest(latest, 'y'),
  )

  return {
    gazeX: useSpring(rawX, AIDA_GAZE_SPRING),
    gazeY: useSpring(rawY, AIDA_GAZE_SPRING),
    lidScale,
    looking,
  }
}
